"use client";

import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Stack, Typography, Grid, InputBase, Box } from "@mui/material";
import theme, { blueGradientSxDown } from "@/theme/theme";
import Image from "next/image";
import SideTabbedButton from "@/components/generic/SideTabbedButton";

import weDevelopIcon1 from "@/assets/images/icons/weDevelopIcon1.webp";
import weDevelopIcon2 from "@/assets/images/icons/weDevelopIcon2.webp";
import weDevelopIcon3 from "@/assets/images/icons/weDevelopIcon3.webp";
import weDevelopIcon4 from "@/assets/images/icons/weDevelopIcon4.webp";
import weDevelopIcon5 from "@/assets/images/icons/weDevelopIcon5.webp";
import weDevelopIcon6 from "@/assets/images/icons/weDevelopIcon6.webp";
import weDevelopIcon7 from "@/assets/images/icons/weDevelopIcon7.webp";
import weDevelopIcon8 from "@/assets/images/icons/weDevelopIcon8.webp";
import weDevelopIconArrow from "@/assets/images/icons/weDevelopIconArrow.svg?url";
import arrow from "@/assets/images/icons/arrow.webp";
import subscribeInputBg from "@/assets/images/subscribeInputBg.webp";

import envelop from "@/assets/images/envelope.webp";

import { useSubscribe } from "@/hooks/useSubscribe";

const DevelopingWithTheBest = () => {
    const [email, setEmail] = useState("");
    const { subscribe, status, error, reset } = useSubscribe({
        onSuccess: () => setEmail(""),
    });

    const loading = status === "loading";

    const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (status !== "idle") reset();
        setEmail(event.target.value);
    };

    const handleSubscribe = () => {
        void subscribe(email);
    };

    // Freeze the subscribe card height when we switch to success (prevents layout jump)
    const subscribeCardRef = useRef<HTMLDivElement | null>(null);
    const [liveCardHeight, setLiveCardHeight] = useState<number | null>(null);
    const [frozenCardHeight, setFrozenCardHeight] = useState<number | null>(null);

    useEffect(() => {
        const el = subscribeCardRef.current;
        if (!el) return;

        // Only observe while we're NOT in success state (so success UI doesn't overwrite the measured height)
        if (status === "success") return;

        const measure = () => {
            const h = Math.round(el.getBoundingClientRect().height);
            if (h > 0) setLiveCardHeight(h);
        };

        measure();

        const ro = new ResizeObserver(() => measure());
        ro.observe(el);

        return () => ro.disconnect();
    }, [status]);

    useEffect(() => {
        if (status === "success") {
            const fallback =
                subscribeCardRef.current
                    ? Math.round(subscribeCardRef.current.getBoundingClientRect().height)
                    : null;

            const h = liveCardHeight ?? fallback;

            if (typeof h === "number" && h > 0) setFrozenCardHeight(h);
        } else {
            setFrozenCardHeight(null);
        }
    }, [status, liveCardHeight]);

    return (
        <Stack
            width="100%"
            height="100%"
            position="relative"
            paddingY={{ xs: 4, lg: 8 }}
            paddingX={{ xs: 2, lg: 10 }}
            gap={4}
        >
            <Stack
                direction={{ xs: "column", lg: "row" }}
                alignItems="stretch"
                justifyContent="space-between"
                gap={4}
            >
                <Stack width={{ xs: "100%", lg: "50%" }} flexGrow={1} position="relative" gap={4}>
                    <Typography
                        variant="h3"
                        fontSize={"2.25rem !important"}
                        fontWeight={400}
                        className="chno desktop-only"
                    >
                        <Box
                            component="span"
                            sx={{
                                ...blueGradientSxDown(theme),
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            Developing With The Best
                        </Box>
                    </Typography>

                    <Typography
                        variant="h3"
                        fontSize={"2.5rem !important"}
                        fontWeight={400}
                        textAlign="center"
                        className="chno mobile-only"
                    >
                        <Box
                            component="span"
                            sx={{
                                ...blueGradientSxDown(theme),
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            Developing<br />With The Best
                        </Box>
                    </Typography>

                    <Grid
                        container
                        spacing={2}
                        width="100%"
                        sx={{
                            "& .weDevelopIcon": {
                                transition: "filter 0.4s ease-in-out, opacity 0.4s ease-in-out",
                                filter: "grayscale(100%)",
                                opacity: 0.5,
                                "&:hover": {
                                    filter: "grayscale(0%)",
                                    opacity: 1,
                                },
                            },
                        }}
                    >
                        {[
                            weDevelopIcon1,
                            weDevelopIcon2,
                            weDevelopIcon3,
                            weDevelopIcon4,
                            weDevelopIcon5,
                            weDevelopIcon6,
                            weDevelopIcon7,
                            weDevelopIcon8,
                        ].map((icon, idx) => (
                            <Grid
                                size={3}
                                key={`weDevelopIcon_${idx}`}
                                sx={{
                                    position: "relative",
                                    "& .weDevelopIconArrow": {
                                        transition: "opacity 0.5s ease-in-out",
                                        opacity: 0,
                                    },
                                    "&:hover .weDevelopIconArrow": {
                                        opacity: 1,
                                    },
                                }}
                            >
                                <Image
                                    src={weDevelopIconArrow}
                                    alt="weDevelopIconArrow"
                                    width={35}
                                    height={35}
                                    className="weDevelopIconArrow"
                                    style={{
                                        position: "absolute",
                                        top: 3,
                                        right: 3,
                                        objectFit: "contain",
                                    }}
                                />
                                <Image src={icon} alt={`weDevelopIcon${idx + 1}`} width={180} height={180} className="weDevelopIcon" />
                            </Grid>
                        ))}
                    </Grid>
                </Stack>

                <Stack
                    sx={{
                        flexGrow: 1,
                        width: { xs: "100%", lg: "2%" },
                        alignItems: "center",
                        background: "linear-gradient(180deg, rgba(0, 19, 153, 0) 0%, #0020FF 100%)",
                    }}
                />

                <Stack width={{ xs: "100%", lg: "48%" }} flexGrow={1} justifyContent="space-between" position="relative" gap={3}>
                    <Typography
                        variant="h3"
                        fontSize={"2.25rem !important"}
                        fontWeight={400}
                        className="chno desktop-only"
                    >
                        <Box
                            component="span"
                            sx={{
                                ...blueGradientSxDown(theme),
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            Subscribe<br />to our newsletter
                        </Box>
                    </Typography>

                    <Typography
                        variant="h3"
                        fontSize={"2.5rem !important"}
                        fontWeight={400}
                        textAlign="center"
                        className="chno mobile-only"
                    >
                         <Box
                            component="span"
                            sx={{
                                ...blueGradientSxDown(theme),
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            Subscribe to<br />our newsletter
                        </Box>
                    </Typography>

                    {/* This is the "card" we freeze the height for */}
                    <Stack
                        ref={subscribeCardRef}
                        width="100%"
                        gap={{ xs: 3, lg: 6 }}
                        marginTop={{ xs: 2, lg: 0 }}
                        sx={{
                            alignItems: { xs: "center", lg: "flex-start" },
                            backgroundColor: { xs: "#101010", lg: "transparent" },
                            borderRadius: { xs: 2, lg: 0 },
                            border: { xs: "1px solid #2F2F2F", lg: "none" },
                            paddingX: { xs: 2, lg: 0 },
                            paddingY: { xs: 3, lg: 0 },

                            // Freeze height when success so the success UI matches the original card height
                            height: status === "success" && frozenCardHeight ? `${frozenCardHeight}px` : "auto",
                            overflow: "hidden",
                        }}
                    >
                        {status === "success" ? (
                            <Stack
                                width="100%"
                                // IMPORTANT: do NOT set height="100%" here; let content size itself inside frozen parent
                                sx={{
                                    backgroundColor: theme.palette.brand.mgsBlue1.main,
                                    borderRadius: { xs: 2, lg: 0 },
                                    padding: { xs: 2.25, sm: 3, lg: 3.25 },
                                    overflow: "hidden",

                                    // Make the success card sit nicely inside the frozen parent height
                                    // (center it, don’t stretch)
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                {/* Title */}
                                <Typography
                                    className="chno"
                                    sx={{
                                        color: "#FFFFFF",
                                        fontSize: { xs: "1.75rem", sm: "2.1rem", lg: "2.2rem" },
                                        lineHeight: 1,
                                        letterSpacing: 1,
                                        mb: { xs: 2, sm: 2.25, lg: 2.25 },
                                        textAlign: "left",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    YOU’RE SUBSCRIBED!
                                </Typography>

                                {/* Row: envelope + text */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="flex-start"
                                    sx={{
                                        width: "100%",
                                        gap: { xs: 2, sm: 3, lg: 3 },
                                    }}
                                >
                                    {/* Envelope (smaller on desktop) */}
                                    <Stack
                                        sx={{
                                            width: { xs: 100, sm: 160, lg: 170 },
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Image
                                            src={envelop}
                                            alt="Subscribed"
                                            priority
                                            style={{
                                                width: "100%",
                                                height: "auto",
                                                objectFit: "contain",
                                                display: "block",
                                            }}
                                        />
                                    </Stack>

                                    {/* Text (clamped on desktop) */}
                                    <Stack
                                        sx={{
                                            flexGrow: 1,
                                            minWidth: 0,
                                            alignItems: "flex-start",
                                            textAlign: "left",
                                            gap: { xs: 1.1, sm: 1.25, lg: 1.25 },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#FFFFFF",
                                                fontSize: { xs: "1.25rem", sm: "1.35rem", lg: "1.35rem" },
                                                fontWeight: 400,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Thank you for joining!
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: "rgba(255,255,255,0.92)",
                                                fontSize: { xs: "0.95rem", sm: "0.98rem", lg: "0.98rem" },
                                                fontWeight: 300,
                                                lineHeight: 1.6,
                                                maxWidth: { xs: "100%", sm: 460, lg: 520 },
                                            }}
                                        >
                                            You’ll now receive the latest updates on AI, blockchain, product launches, and expert insights straight to your inbox.
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Stack>
                        ) : (
                            <>
                                <Stack gap={{ xs: 3, lg: 2 }}>
                                    <Typography
                                        variant="h5"
                                        fontWeight={400}
                                        textAlign={{ xs: "center", lg: "left" }}
                                        sx={{
                                            fontSize: { xs: "1.2rem !important", lg: "1.5rem !important" },
                                        }}
                                    >
                                        Join our community of innovators.
                                    </Typography>

                                    <Typography
                                        variant="body1"
                                        color={theme.palette.text.secondary}
                                        fontWeight={400}
                                        textAlign={{ xs: "center", lg: "left" }}
                                        sx={{
                                            width: { xs: "100%", lg: "80%" },
                                            fontSize: { xs: "1.1rem !important", lg: "1rem !important" },
                                        }}
                                    >
                                        Get the latest updates on AI, blockchain, and product launches — plus expert tips to help your business grow.
                                    </Typography>
                                </Stack>

                                <Stack
                                    width={{ xs: "100%", lg: "80%" }}
                                    direction={{ xs: "column", lg: "row" }}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={{ xs: 1.5, lg: 1 }}
                                >
                                    <Stack
                                        alignItems={{ xs: "center", lg: "flex-start" }}
                                        justifyContent={{ xs: "center", lg: "center" }}
                                        paddingY={1}
                                        width="100%"
                                        height="100%"
                                        position="relative"
                                        overflow="hidden"
                                        sx={{
                                            backgroundImage: `url(${subscribeInputBg.src})`,
                                            backgroundSize: "100% 90%",
                                            backgroundPosition: "center center",
                                            backgroundRepeat: "no-repeat",
                                            opacity: 0.75,
                                            transition: "opacity 0.3s ease-in-out",
                                            "&:hover": {
                                                opacity: 1,
                                            },
                                        }}
                                    >
                                        <InputBase
                                            value={email}
                                            onChange={handleEmailChange}
                                            placeholder="Your email"
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                borderRadius: 1,
                                                border: "none",
                                                outline: "none",
                                                fontWeight: 300,
                                                position: "relative",
                                                paddingX: 1,
                                                zIndex: 1,
                                                "& ::placeholder": {
                                                    fontWeight: 300,
                                                },
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !loading) handleSubscribe();
                                            }}
                                        />
                                    </Stack>

                                    <Stack width="auto" display={{ xs: "none", lg: "flex" }}>
                                        <SideTabbedButton paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} action={handleSubscribe}>
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography component="span" variant="h6" marginRight={0.5}>
                                                    Subscribe
                                                </Typography>
                                                <span className="arrow-clip">
                                                    <span className="arrow-slide">
                                                        <Image
                                                            src={arrow}
                                                            alt="arrow animation"
                                                            width={20}
                                                            height={20}
                                                            style={{ display: "block" }}
                                                        />
                                                    </span>
                                                </span>
                                            </div>
                                        </SideTabbedButton>
                                    </Stack>

                                    <Stack width="100%" display={{ xs: "flex", lg: "none" }}>
                                        <SideTabbedButton
                                            fullWidth
                                            paddingX={18}
                                            hoverShiftX={1.5}
                                            hoverShiftY={-1}
                                            action={handleSubscribe}
                                        >
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography component="span" variant="h6" marginRight={0.5}>
                                                    Subscribe
                                                </Typography>
                                                <span className="arrow-clip">
                                                    <span className="arrow-slide">
                                                        <Image
                                                            src={arrow}
                                                            alt="arrow animation"
                                                            width={20}
                                                            height={20}
                                                            style={{ display: "block" }}
                                                        />
                                                    </span>
                                                </span>
                                            </div>
                                        </SideTabbedButton>
                                    </Stack>
                                </Stack>

                                {/* feedback */}
                                {status === "error" && !!error && (
                                    <Typography role="status" variant="body2" color="error.main" sx={{ mt: -1 }} aria-live="polite">
                                        {error}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default DevelopingWithTheBest;
